import * as React from 'react';

import type { ParentFolders } from '../../api/types/document.types';
import type {
  DocumentsFilterContextValue,
  DocumentsFilterProviderProps,
  DocumentsFilters,
} from './DocumentsFilterContext.types';

const INITIAL_FILTERS: DocumentsFilters = {
  searchText: '',
  folderId: null,
  selectedOrganizationId: '',
  parentFolders: [],
};

const DocumentsFilterContext = React.createContext<
  DocumentsFilterContextValue | undefined
>(undefined);

const DocumentsFilterProvider = React.memo(
  ({
    children,
  }: Readonly<DocumentsFilterProviderProps>): React.ReactElement => {
    const [filters, setFiltersState] =
      React.useState<DocumentsFilters>(INITIAL_FILTERS);

    const isFiltersApplied = filters.searchText.length > 0;

    const setFilters = React.useCallback(
      (partial: Partial<DocumentsFilters>): void => {
        setFiltersState(prev => {
          const next = { ...prev, ...partial };
          if (
            partial.selectedOrganizationId !== undefined &&
            partial.selectedOrganizationId !== prev.selectedOrganizationId
          ) {
            next.folderId = null;
            next.parentFolders = [];
            next.searchText = '';
          }
          return next;
        });
      },
      []
    );

    const clearFilters = React.useCallback((): void => {
      setFiltersState(INITIAL_FILTERS);
    }, []);

    const navigateToFolder = React.useCallback(
      (folderId: string, parentFolders: ParentFolders[]): void => {
        setFiltersState(prev => ({
          ...prev,
          folderId,
          parentFolders,
        }));
      },
      []
    );

    const navigateToRoot = React.useCallback((): void => {
      setFiltersState(prev => ({
        ...prev,
        folderId: null,
        parentFolders: [],
      }));
    }, []);

    const navigateToBreadcrumb = React.useCallback(
      (folderId: string | null, breadcrumbIndex: number): void => {
        setFiltersState(prev => ({
          ...prev,
          folderId,
          parentFolders: prev.parentFolders.slice(0, breadcrumbIndex + 1),
        }));
      },
      []
    );

    const value: DocumentsFilterContextValue = React.useMemo(
      () => ({
        filters,
        isFiltersApplied,
        setFilters,
        clearFilters,
        navigateToFolder,
        navigateToRoot,
        navigateToBreadcrumb,
      }),
      [
        filters,
        isFiltersApplied,
        setFilters,
        clearFilters,
        navigateToFolder,
        navigateToRoot,
        navigateToBreadcrumb,
      ]
    );

    return (
      <DocumentsFilterContext value={value}>
        {children}
      </DocumentsFilterContext>
    );
  }
);

DocumentsFilterProvider.displayName = 'DocumentsFilterProvider';

export { DocumentsFilterContext, DocumentsFilterProvider };
